import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdBlock,
  MdPhone,
  MdEmail,
} from "react-icons/md";
import {
  TbShieldCheck,
  TbClockHour4,
} from "react-icons/tb";
import { FiAlertTriangle, FiCopy } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import toast from "react-hot-toast";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import PenaltyPaymentModal from "./PenaltyPaymentModal";

const SupplierPenaltyWarningModal = ({
  isOpen,
  onClose,
  penalties = [],
  isAppAccessBlocked = false,
  user,
  onPaymentSuccess,
  onPayLater,
}) => {
  const [animate, setAnimate] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showSupportOptions, setShowSupportOptions] = useState(false);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setAnimate(true), 10);
      return () => clearTimeout(timer);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  // Group blocking vs upcoming
  const { blockingPenalties, upcomingPenalties } = useMemo(() => {
    const blocking = [];
    const upcoming = [];

    penalties.forEach((p) => {
      if (p.isBlocking || p.isPastDue) {
        blocking.push(p);
      } else {
        upcoming.push(p);
      }
    });

    return { blockingPenalties: blocking, upcomingPenalties: upcoming };
  }, [penalties]);

  const totalAmount = useMemo(
    () => penalties.reduce((sum, p) => sum + (p.amounts?.totalAmount || 0), 0),
    [penalties]
  );

  const isBlocked = isAppAccessBlocked || blockingPenalties.length > 0;

  const handleClose = () => {
    if (isBlocked) return;
    setAnimate(false);
    setTimeout(() => {
      if (onPayLater) {
        onPayLater();
      } else if (onClose) {
        onClose();
      }
    }, 250);
  };

  const handlePayOnTheSpot = () => {
    setIsPaymentModalOpen(true);
  };

  const handlePaymentCompleted = () => {
    setIsPaymentModalOpen(false);
    onPaymentSuccess && onPaymentSuccess();
  };

  const copyId = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Penalty ID copied!");
  };

  if (!isOpen || penalties.length === 0) return null;

  return (
    <>
      {createPortal(
        <div
          className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 font-Urbanist ${
            animate ? "opacity-100" : "opacity-0"
          }`}
          onClick={!isBlocked ? handleClose : undefined}
        >
          <div
            className={`bg-white dark:bg-gray-900 w-full md:w-[580px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
              isBlocked ? "border-t-4 md:border-2 border-red-500" : ""
            } ${
              animate
                ? "scale-100 translate-y-0"
                : "scale-95 md:translate-y-4 translate-y-full"
            }`}
            style={{ maxHeight: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className={`px-5 py-4 ${
                isBlocked ? "bg-red-600" : "bg-primaryColor"
              } text-white flex items-center justify-between flex-shrink-0 shadow-sm`}
            >
              <div className="flex items-center gap-2.5">
                {!isBlocked ? (
                  <button
                    onClick={handleClose}
                    className="text-white hover:text-white/80 transition-colors p-1 -ml-1 rounded-full hover:bg-white/10"
                    title="Back"
                  >
                    <span className="text-xl leading-none">←</span>
                  </button>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                    <MdBlock size={16} className="text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-base sm:text-lg font-bold tracking-wide leading-tight">
                    {isBlocked
                      ? "Account Access Restricted"
                      : "Pending Penalties Notice"}
                  </h2>
                  <p className="text-[11px] text-white/80 font-medium">
                    {isBlocked
                      ? "Immediate settlement required • Platform access restricted"
                      : "Review and settle outstanding dues"}
                  </p>
                </div>
              </div>

              {!isBlocked && (
                <button
                  onClick={handleClose}
                  className="text-white hover:text-white/80 transition-colors p-1.5 rounded-full hover:bg-white/10"
                  title="Close"
                >
                  <MdClose size={20} />
                </button>
              )}
            </div>

            {/* Scrollable Body */}
            <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4 no-scrollbar">
              {/* Notice Box */}
              <div
                className={`p-4 rounded-2xl border flex flex-col gap-3 ${
                  isBlocked
                    ? "bg-red-50/70 dark:bg-red-950/20 border-red-200 dark:border-red-900/50"
                    : "bg-amber-50/70 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isBlocked
                        ? "bg-red-600 text-white shadow-md shadow-red-500/20"
                        : "bg-amber-500 text-white shadow-md shadow-amber-500/20"
                    }`}
                  >
                    {isBlocked ? <MdBlock size={20} /> : <FiAlertTriangle size={18} />}
                  </div>
                  <div className="flex-1">
                    <h3
                      className={`text-sm font-extrabold ${
                        isBlocked
                          ? "text-red-700 dark:text-red-400"
                          : "text-amber-800 dark:text-amber-300"
                      }`}
                    >
                      {isBlocked
                        ? "Account Access Restricted"
                        : "Pending Dues Notice"}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-relaxed">
                      {isBlocked
                        ? "Unpaid overdue penalties have restricted your ability to submit quotations and access live orders. Settle your dues below to restore instant platform access."
                        : "You have pending penalties from returns or disputes. You can pay on the spot via Razorpay or choose to pay later before the due date."}
                    </p>
                  </div>
                </div>

                {/* Total Outstanding Dues Card */}
                <div
                  className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 ${
                    isBlocked
                      ? "bg-white dark:bg-gray-800 border-red-100 dark:border-red-900/30"
                      : "bg-white dark:bg-gray-800 border-amber-100 dark:border-amber-900/30"
                  }`}
                >
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block">
                      Total Outstanding Dues
                    </span>
                    <span
                      className={`text-2xl font-black ${
                        isBlocked ? "text-red-600" : "text-[#772037]"
                      }`}
                    >
                      ₹{totalAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                      {penalties.length} {penalties.length === 1 ? "Penalty" : "Penalties"}
                    </span>
                    {blockingPenalties.length > 0 && (
                      <span className="text-[11px] font-extrabold text-red-600 dark:text-red-400 block">
                        {blockingPenalties.length} Overdue / Blocking
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Penalties List */}
              <div className="space-y-3">
                {/* Blocking penalties */}
                {blockingPenalties.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-extrabold text-red-600 uppercase tracking-wider">
                      <MdBlock size={14} />
                      <span>Overdue / Blocking ({blockingPenalties.length})</span>
                    </div>
                    {blockingPenalties.map((penalty) => (
                      <div
                        key={penalty._id}
                        className="p-3.5 rounded-xl bg-red-50/60 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 text-xs space-y-1.5"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-gray-900 dark:text-white">
                              {penalty.uniquePenaltyId || `#${penalty._id.slice(-6).toUpperCase()}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyId(penalty.uniquePenaltyId || penalty._id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Copy ID"
                            >
                              <FiCopy size={12} />
                            </button>
                          </div>
                          <span className="text-sm font-black text-red-600">
                            ₹{(penalty.amounts?.totalAmount || 0).toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-gray-500">
                          <span>
                            {penalty.penaltyReason?.replace(/_/g, " ") ||
                              penalty.penaltyType?.replace(/_/g, " ") ||
                              "Return Penalty"}
                          </span>
                          <span className="text-red-600 font-extrabold">
                            {penalty.isPastDue ? "Overdue" : "Account Blocked"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upcoming penalties */}
                {upcomingPenalties.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-extrabold text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                      <TbClockHour4 size={14} />
                      <span>Upcoming Dues ({upcomingPenalties.length})</span>
                    </div>
                    {upcomingPenalties.map((penalty) => (
                      <div
                        key={penalty._id}
                        className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 text-xs space-y-1.5"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-gray-800 dark:text-gray-200">
                              {penalty.uniquePenaltyId || `#${penalty._id.slice(-6).toUpperCase()}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyId(penalty.uniquePenaltyId || penalty._id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Copy ID"
                            >
                              <FiCopy size={12} />
                            </button>
                          </div>
                          <span className="text-sm font-black text-[#772037]">
                            ₹{(penalty.amounts?.totalAmount || 0).toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-gray-500">
                          <span>
                            {penalty.penaltyReason?.replace(/_/g, " ") ||
                              penalty.penaltyType?.replace(/_/g, " ") ||
                              "Return Penalty"}
                          </span>
                          <span className="font-bold text-amber-600">
                            {penalty.daysUntilDue <= 1
                              ? "Due Soon"
                              : `Due in ${penalty.daysUntilDue} days`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Support options collapsible */}
              {showSupportOptions && (
                <div className="pt-2 animate-fadeIn">
                  <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 mb-2">
                    Need immediate assistance with penalty clearing?
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => window.open("tel:+918339903307", "_self")}
                      className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-1.5 font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-100 transition"
                    >
                      <MdPhone size={14} className="text-[#772037]" />
                      <span>Call Us</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          "https://wa.me/918339903307?text=Hello%20PCM%20Exports%20Team%2C%20I%20need%20assistance%20with%20my%20penalties.",
                          "_blank"
                        )
                      }
                      className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-1.5 font-bold text-gray-700 dark:text-gray-200 hover:bg-emerald-50 transition"
                    >
                      <FaWhatsapp size={14} className="text-emerald-500" />
                      <span>WhatsApp</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          "mailto:pcmexportsbbsr@gmail.com?subject=Penalty%20Payment%20Query",
                          "_self"
                        )
                      }
                      className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-1.5 font-bold text-gray-700 dark:text-gray-200 hover:bg-blue-50 transition"
                    >
                      <MdEmail size={14} className="text-blue-500" />
                      <span>Email</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/50 flex-shrink-0 space-y-2.5">
              <div className="flex flex-col sm:flex-row items-center gap-2.5">
                {!isBlocked ? (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="w-full sm:w-auto flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-bold text-xs transition"
                  >
                    I'll Pay Later
                  </button>
                ) : (
                  <div className="w-full sm:w-auto flex-1 text-center sm:text-left text-[11px] font-bold text-red-600 flex items-center justify-center sm:justify-start gap-1">
                    <MdBlock size={13} />
                    <span>Unblock requires payment</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handlePayOnTheSpot}
                  className="w-full sm:w-auto flex-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] hover:from-[#651a2e] hover:to-[#772037] text-white font-extrabold text-xs sm:text-sm shadow-md shadow-[#772037]/25 flex items-center justify-center gap-2 transition"
                >
                  <TbShieldCheck size={18} />
                  <span>
                    {isBlocked
                      ? `Pay ₹${totalAmount.toLocaleString("en-IN")} to Unblock`
                      : `Pay on the Spot (₹${totalAmount.toLocaleString("en-IN")})`}
                  </span>
                </button>
              </div>

              <div className="flex items-center justify-between text-[11px] text-gray-400 pt-1">
                <span>Secured by Razorpay • Instant Auto-Clearance</span>
                <button
                  type="button"
                  onClick={() => setShowSupportOptions((prev) => !prev)}
                  className="text-primaryColor hover:underline font-semibold"
                >
                  {showSupportOptions ? "Hide Support" : "Need Help?"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Razorpay Checkout Modal */}
      <PenaltyPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedPenalties={penalties}
        user={user}
        onSuccess={handlePaymentCompleted}
      />
    </>
  );
};

export default SupplierPenaltyWarningModal;
